import { acetateSpec, type AcetateSpec } from '../config/acetates';
import { TEMPLE_INFO, templeImageUrl } from './zones';

/**
 * A110Z — the temple photo of a model, recoloured in the acetate chosen in Couleurs.
 *
 * A110Z3 (David : « utilise les vraies images de branche ») : the real detoured photo (assets-source/temples-hd/,
 * sharpened where the product photo was small), shown as shot for « Original ». For another acetate, the photo's
 * shading (highlights, the metal core, the polished edges) is kept as a luminosity layer and multiplied over the
 * plate — the plate photo of the library at its true scale for patterned acetates, the plate colour for the
 * others (as in A110Z). A preview of the material, like the 3D frame: « Matières à confirmer avec l'atelier ».
 *
 * Each image also carries the temple's outline (top and bottom edge per column, from the photo's alpha), which
 * tells whether a text placed by hand stays on the temple, and the decoded bitmap for the order sheet.
 * Recoloured results are blob URLs, cached per (model, acetate) and released when the cache turns over.
 */
export interface TempleOutline {
  /** Top and bottom edge per column, as fractions of the height (−1 where the column is empty). */
  top: Float32Array;
  bot: Float32Array;
}

export interface TempleImage {
  url: string;
  width: number;
  height: number;
  outline: TempleOutline | null;
  bitmap: ImageBitmap | HTMLImageElement | null;
}

/** Longest side of the recoloured canvas (the HD photos are ≈ 2400 px at 16 px/mm). */
const MAX_WIDTH = 2400;
/** Columns of the outline. */
const OUTLINE_COLUMNS = 600;
const CACHE_MAX = 8;
/** Shading range kept from the photo (ratio to its mean luminance). */
const SHADE_MIN = 0.35;
const SHADE_MAX = 1.75;
const SHADE_GAMMA = 0.85;

const cache = new Map<string, Promise<TempleImage | null>>();
const bitmaps = new Map<string, Promise<ImageBitmap | HTMLImageElement | null>>();
const outlines = new Map<string, TempleOutline | null>();

function loadBitmap(url: string): Promise<ImageBitmap | HTMLImageElement | null> {
  let p = bitmaps.get(url);
  if (!p) {
    p = (async () => {
      try {
        if (typeof createImageBitmap === 'function') {
          const res = await fetch(url);
          if (!res.ok) return null;
          return await createImageBitmap(await res.blob());
        }
      } catch {
        // fall through to an <img>
      }
      return new Promise<HTMLImageElement | null>(resolve => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = url;
      });
    })();
    bitmaps.set(url, p);
  }
  return p;
}

function sizeOf(img: ImageBitmap | HTMLImageElement): { w: number; h: number } {
  return 'naturalWidth' in img ? { w: img.naturalWidth, h: img.naturalHeight } : { w: img.width, h: img.height };
}

/** Top / bottom edge of the temple per column, from the alpha of a reduced copy. */
function measureOutline(img: ImageBitmap | HTMLImageElement): TempleOutline | null {
  const { w: sw, h: sh } = sizeOf(img);
  if (!sw || !sh) return null;
  const w = Math.min(OUTLINE_COLUMNS, sw);
  const h = Math.max(1, Math.round((sh * w) / sw));
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(img as CanvasImageSource, 0, 0, w, h);
  const d = ctx.getImageData(0, 0, w, h).data;
  const top = new Float32Array(w).fill(-1), bot = new Float32Array(w).fill(-1);
  for (let x = 0; x < w; x++) {
    let t = -1, b = -1;
    for (let y = 0; y < h; y++) if (d[(y * w + x) * 4 + 3]! > 127) { t = y; break; }
    for (let y = h - 1; y >= 0; y--) if (d[(y * w + x) * 4 + 3]! > 127) { b = y; break; }
    if (t >= 0 && b > t) { top[x] = t / h; bot[x] = (b + 1) / h; }
  }
  canvas.width = 0; canvas.height = 0;
  return { top, bot };
}

/** The photo's bitmap and outline (cached per model). */
async function photoOf(templeId: string): Promise<{ url: string; bitmap: ImageBitmap | HTMLImageElement; outline: TempleOutline | null } | null> {
  const url = templeImageUrl(templeId);
  if (!url) return null;
  const bitmap = await loadBitmap(url);
  if (!bitmap) return null;
  if (!outlines.has(templeId)) outlines.set(templeId, measureOutline(bitmap));
  return { url, bitmap, outline: outlines.get(templeId) ?? null };
}

/**
 * Is the point (x, y) of the displayed image on the temple, at least `margin` px inside its edges? The image is
 * W × H, mirrored or not. True when the outline is unknown (nothing to check against).
 */
export function onTemple(outline: TempleOutline | null, W: number, H: number, mirrored: boolean, x: number, y: number, margin = 0): boolean {
  if (!outline) return true;
  const n = outline.top.length;
  const xi = mirrored ? W - x : x;
  const col = Math.floor((xi / W) * n);
  if (col < 0 || col >= n) return false;
  const t = outline.top[col]!, b = outline.bot[col]!;
  if (t < 0) return false;
  return y >= t * H + margin && y <= b * H - margin;
}

function hexToRgb(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return [128, 128, 128];
  return [0, 2, 4].map(i => parseInt(m[1]!.slice(i, i + 2), 16)) as [number, number, number];
}

/** Draws the plate layer (pattern at true scale, or flat colour) over the whole canvas. */
async function drawPlate(ctx: CanvasRenderingContext2D, w: number, h: number, spec: AcetateSpec, pxPerMm: number): Promise<void> {
  ctx.fillStyle = spec.color;
  ctx.fillRect(0, 0, w, h);
  if (!spec.texture || !spec.textureMm) return;
  const plate = await loadBitmap(spec.texture);
  if (!plate) return;
  const ps = sizeOf(plate);
  if (!ps.w || !ps.h) return;
  // Plate pixels → mm → temple pixels.
  const scale = (spec.textureMm[0] / ps.w) * pxPerMm;
  const pattern = ctx.createPattern(plate as CanvasImageSource, 'repeat');
  if (!pattern) return;
  const matrix = typeof DOMMatrix === 'function' ? new DOMMatrix().scale(scale, scale) : null;
  if (matrix && typeof pattern.setTransform === 'function') pattern.setTransform(matrix);
  ctx.fillStyle = pattern;
  ctx.fillRect(0, 0, w, h);
}

async function recolour(templeId: string, spec: AcetateSpec): Promise<TempleImage | null> {
  const photo = await photoOf(templeId);
  if (!photo) return null;
  const src = sizeOf(photo.bitmap);
  if (!src.w || !src.h) return null;
  const k = Math.min(1, MAX_WIDTH / src.w);
  const w = Math.max(1, Math.round(src.w * k)), h = Math.max(1, Math.round(src.h * k));
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(photo.bitmap as CanvasImageSource, 0, 0, w, h);
  const shade = ctx.getImageData(0, 0, w, h);
  const d = shade.data;
  // Mean luminance of the temple (opaque pixels), the reference of the shading.
  let sum = 0, n = 0;
  for (let i = 0; i < d.length; i += 4) {
    if (d[i + 3]! < 128) continue;
    sum += 0.299 * d[i]! + 0.587 * d[i + 1]! + 0.114 * d[i + 2]!; n++;
  }
  const mean = n ? Math.max(24, sum / n) : 128;
  const info = TEMPLE_INFO[templeId];
  // The HD photo spans the same millimetres as the measured one: its own px per mm.
  const pxPerMm = info?.mmPerPx && info.w ? w / (info.w * info.mmPerPx) : w / 147;
  ctx.clearRect(0, 0, w, h);
  await drawPlate(ctx, w, h, spec, pxPerMm);
  const out = ctx.getImageData(0, 0, w, h);
  const o = out.data;
  const [fr, fg, fb] = hexToRgb(spec.color);
  for (let i = 0; i < d.length; i += 4) {
    const a = d[i + 3]!;
    if (a === 0) { o[i + 3] = 0; continue; }
    const luma = 0.299 * d[i]! + 0.587 * d[i + 1]! + 0.114 * d[i + 2]!;
    const ratio = Math.min(SHADE_MAX, Math.max(SHADE_MIN, luma / mean));
    const s = Math.pow(ratio, SHADE_GAMMA);
    // Highlights above the plate turn towards white (polished acetate, metal core), not towards saturation.
    const lift = s > 1 ? (s - 1) * 0.35 : 0;
    o[i] = Math.min(255, (o[i] ?? fr) * s + 255 * lift);
    o[i + 1] = Math.min(255, (o[i + 1] ?? fg) * s + 255 * lift);
    o[i + 2] = Math.min(255, (o[i + 2] ?? fb) * s + 255 * lift);
    o[i + 3] = a;
  }
  ctx.putImageData(out, 0, 0);
  const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png'));
  canvas.width = 0; canvas.height = 0;
  if (!blob) return null;
  const url = URL.createObjectURL(blob);
  const bitmap = await loadBitmap(url);
  return { url, width: w, height: h, outline: photo.outline, bitmap };
}

/**
 * Image of the temple of `templeId` in `acetateId` (`original` = the photo). Null when the model
 * has no temple photo. Cached; the oldest recoloured images are released beyond `CACHE_MAX`.
 */
export function templeImage(templeId: string, acetateId: string): Promise<TempleImage | null> {
  if (!templeImageUrl(templeId)) return Promise.resolve(null);
  const spec = acetateId === 'original' ? undefined : acetateSpec(acetateId);
  const key = `${templeId}|${spec?.id ?? 'original'}`;
  let p = cache.get(key);
  if (!p) {
    p = spec
      ? recolour(templeId, spec).catch(() => null)
      : photoOf(templeId).then(ph => {
        if (!ph) return null;
        const { w, h } = sizeOf(ph.bitmap);
        return { url: ph.url, width: w, height: h, outline: ph.outline, bitmap: ph.bitmap };
      }).catch(() => null);
    cache.set(key, p);
    if (cache.size > CACHE_MAX) {
      const [oldKey, oldP] = cache.entries().next().value as [string, Promise<TempleImage | null>];
      cache.delete(oldKey);
      // Released a little later: a card still showing it has time to ask for a fresh image.
      void oldP.then(img => {
        if (img && img.url.startsWith('blob:')) window.setTimeout(() => { URL.revokeObjectURL(img.url); bitmaps.delete(img.url); }, 30000);
      });
    }
  }
  return p;
}

/** Releases every recoloured image (tests, page teardown). */
export function clearTempleImages(): void {
  for (const p of cache.values()) void p.then(img => { if (img && img.url.startsWith('blob:')) URL.revokeObjectURL(img.url); });
  cache.clear();
  bitmaps.clear();
  outlines.clear();
}
